import React from "react";
import axios from "axios";
import { useCart } from "../components/LandingLayout";
import { NotifyBack } from "../components/NotifyBack";
import { ProductModal } from "../components/ProductModal";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Placeholder() {
  return (
    <div className="flex h-56 w-full items-center justify-center bg-gradient-to-br from-[#f3ece8] to-[#e7dcd6]">
      <span className="text-[11px] uppercase tracking-[0.3em] text-neutral-400">
        Lash Dollz
      </span>
    </div>
  );
}

export default function ShopPage() {
  const { addToCart } = useCart();
  const [selected, setSelected] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (!cancelled) setProducts(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setError("Products are temporarily unavailable.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="pb-12" data-testid="shop-page">
      <header className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
            Shop
          </p>
          <h1 className="text-2xl font-medium tracking-tight text-neutral-900">
            Lash Dollz Vk Beauty — Pro Supplies
          </h1>
        </div>
        <p className="max-w-sm text-xs text-neutral-500">
          Studio-tested lash products & tools, shipped across Ireland or ready
          to collect in Tralee. Secure checkout with card.
        </p>
      </header>

      {loading && <p className="text-xs text-neutral-500">Loading products…</p>}
      {error && !loading && <p className="text-xs text-neutral-500">{error}</p>}

      {!loading && !error && (
        <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <article
              key={product.id}
              data-testid={`shop-product-${product.id}`}
              className="group flex flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white"
            >
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  onClick={() => setSelected(product)}
                  data-testid={`view-product-${product.id}`}
                  className="h-56 w-full cursor-pointer object-cover transition-transform duration-300 group-hover:scale-105"
                />
              ) : (
                <button type="button" onClick={() => setSelected(product)} className="text-left">
                  <Placeholder />
                </button>
              )}
              <div className="flex flex-1 flex-col px-4 py-4 text-sm">
                <h3
                  onClick={() => setSelected(product)}
                  className="cursor-pointer text-neutral-900 hover:underline"
                >
                  {product.name}
                </h3>
                {product.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-neutral-500">
                    {product.description}
                  </p>
                )}
                <div className="mt-3 flex items-baseline gap-2 text-sm">
                  <span className="font-medium text-neutral-900">
                    €{Number(product.price).toFixed(2)}
                  </span>
                  {product.stock === 0 && (
                    <span
                      data-testid={`sold-out-${product.id}`}
                      className="text-[11px] font-medium uppercase tracking-[0.2em] text-red-500"
                    >
                      Sold out
                    </span>
                  )}
                  {product.stock > 0 && product.stock <= 5 && (
                    <span
                      data-testid={`low-stock-${product.id}`}
                      className="text-[11px] font-medium uppercase tracking-[0.2em] text-amber-600"
                    >
                      Only {product.stock} left
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`add-to-cart-${product.id}`}
                  disabled={product.stock === 0}
                  onClick={() => {
                    if (product.stock === 0) return;
                    addToCart({
                      id: product.id,
                      name: product.name,
                      price: Number(product.price),
                    });
                    toast.success(`${product.name} added to cart`);
                  }}
                  className="mt-4 inline-flex h-9 items-center justify-center rounded-full border border-neutral-900 px-6 text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-900 transition-colors duration-150 hover:bg-neutral-900 hover:text-white disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-300 disabled:hover:bg-transparent disabled:hover:text-neutral-300"
                >
                  {product.stock === 0 ? "Sold out" : "Add to cart"}
                </button>
                {product.stock === 0 && (
                  <NotifyBack productId={product.id} testidPrefix="notify" />
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      {selected && (
        <ProductModal product={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
